import React, { useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../ui/Button';
import ConditionBuilder from '../../../common/ConditionBuilder';
import { Condition } from '../../../../types/segments/segments.types';
import { validateConditions } from '../../../../utils/conditionValidation';
import {
  getCampaignContactCountsRequest,
  selectCampaignContactCounts,
  selectCampaignContactCountsLoading,
} from '../../../../store/campaigns';

interface TriggerConditionPanelProps {
  onClose: () => void;
  onBack: () => void;
  onSave: (conditions: any) => void;
  initialConditions?: { [key: string]: Condition[] };
  onConditionsChange?: (conditions: { [key: string]: Condition[] }) => void;
  conditionsModified?: boolean;
  frequencyModified?: boolean;
  hideBackButton?: boolean;
  campaignId?: string;
}

const TriggerConditionPanel: React.FC<TriggerConditionPanelProps> = ({
  onClose,
  onBack,
  onSave,
  initialConditions,
  onConditionsChange,
  conditionsModified = false,
  frequencyModified = false,
  hideBackButton = false,
  campaignId,
}) => {
  const dispatch = useDispatch();
  const [conditions, setConditions] = useState<{ [key: string]: Condition[] }>(
    initialConditions || { and: [] }
  );
  const contactCounts = useSelector(selectCampaignContactCounts);
  const contactCountsLoading = useSelector(selectCampaignContactCountsLoading);

  const handleConditionsChange = (newConditions: {
    [key: string]: Condition[];
  }) => {
    setConditions(newConditions);
    if (onConditionsChange) {
      onConditionsChange(newConditions);
    }
  };

  // Fetch contact counts when conditions are valid and campaignId is available
  useEffect(() => {
    const validation = validateConditions(conditions);
    if (
      campaignId &&
      validation.isValid &&
      (conditions.and?.length > 0 || conditions.or?.length > 0)
    ) {
      dispatch(getCampaignContactCountsRequest(campaignId));
    }
  }, [conditions, campaignId, dispatch]);

  const validation = validateConditions(conditions);

  const handleSave = () => {
    if (validation.isValid) {
      onSave({
        conditions,
        status: 'configured',
      });
    }
  };

  return (
    <div className="flex flex-col bg-card rounded-b-lg shadow-sm w-full h-full overflow-y-auto">
      <div className="flex-1 p-4 w-full space-y-4 overflow-y-auto min-h-0">
        <div className="w-full space-y-4">
          <ConditionBuilder
            conditions={conditions}
            onConditionsChange={handleConditionsChange}
            fieldErrors={validation.fieldErrors}
            dataSourceOptions={{
              Segment: [
                {
                  value: 'segment',
                  label: 'Segment',
                },
              ],
            }}
          />

          {/* Contact count - card-style result block */}
          {validation.isValid &&
            (conditions.and?.length > 0 || conditions.or?.length > 0) && (
              <div className="rounded-lg border border-border bg-muted/20 px-3 py-2.5">
                {contactCountsLoading ? (
                  <p className="text-xs text-muted-foreground">
                    <span className="text-primary font-medium">Loading...</span>{' '}
                    matches the trigger conditions.
                  </p>
                ) : contactCounts?.totalContacts !== undefined ? (
                  <p className="text-xs">
                    <span className="font-semibold text-primary">
                      {contactCounts.totalContacts}{' '}
                      {contactCounts.totalContacts === 1
                        ? 'person'
                        : 'contacts'}
                    </span>
                    <span className="text-muted-foreground">
                      {' '}
                      matches the trigger conditions.
                    </span>
                  </p>
                ) : null}
              </div>
            )}
        </div>
      </div>

      <div className="w-full p-4 border-t border-border shrink-0 space-y-3 bg-muted/10">
        <Button
          variant="primary"
          disabled={
            !validation.isValid || (!conditionsModified && !frequencyModified)
          }
          onClick={handleSave}
          className="w-full bg-primary text-primary-foreground hover:bg-primary-hover border-0 text-sm font-medium py-2.5 rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Save and Continue
        </Button>
        <div className="flex items-center gap-2">
          {!hideBackButton && (
            <Button
              variant="outline"
              onClick={onBack}
              className="flex-1 flex items-center justify-center gap-2 border-border text-foreground hover:bg-muted/80 text-sm py-2 rounded-lg bg-card"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </Button>
          )}
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-border text-foreground hover:bg-muted/80 text-sm py-2 rounded-lg bg-card"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TriggerConditionPanel;
